<template>
    <div>
        <div class="flex justify-between" :class="{ 'pb-4 modal-toolbar': isForProject }">
            <div v-if="showHeader" class="flex" :class="[smallHeader ? 'my-3' : 'mb-5']">
                <h1 class="page-header" :class="{ 'text-2xl': smallHeader }">Buildings</h1>
            </div>
            <div v-if="showFilters" class="flex items-center" :class="{'flex-1 justify-end pt-4 pb-3': ! showHeader}">
                <filters
                    data-type="buildings"
                    sort-by="address_line_1"
                    :show-client-filter="false"
                    :show-view-options="false"
                    open-search
                    portal="no-buildings-found"
                    :filters="filters"
                    @view-change="layout = $event"
                    @searching="searching = $event">

                    <template slot="filters">
                        <div class="flex items-center mb-4">
                            <div class="flex flex-col leading-normal">
                                <span>Available Sq Ft</span>
                                <div v-if="availableSqFtFilter > 0" class="font-semibold">&gt;= {{ availableSqFtFilter | number }}</div>
                            </div>
                            <input v-model="availableSqFtFilter" type="range" min="0" max="1000000" step="10000" value="0" class="range-green">
                        </div>
                        <div class="flex items-center">
                            <span>For Sale</span>
                            <toggle-button v-model="forSaleFilter" color="#38C172" sync></toggle-button>
                        </div>
                    </template>
                </filters>
                <div v-if="showCreateNew" class="ml-8 flex items-center">
                    <a href="/buildings/wizard" class="flex items-center btn btn-small btn-indigo">
                        <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                        <span class="ml-2">Create</span>
                    </a>
                    <a href="/buildings/import" class="hidden ml-2 flex items-center btn btn-small btn-indigo">
                        <inline-svg name="upload" classes="w-4 h-4 text-white fill-current"></inline-svg>
                        <span class="ml-2">Import</span>
                    </a>
                </div>
            </div>
        </div>

        <div :class="{ 'py-4 px-10': buildings.filtered.length && isForProject }">
            <div v-if="layout == 'grid'">
                <paginate name="buildings" :list="buildings.filtered" :per="perPage" class="paginate-list">
                    <li class="card" v-for="(building, buildingIndex) in paginated('buildings')" :key="buildingIndex">
                        <a v-bind:href="building.edit_url">
                            <div class="box box-blue">
                                <div class="flex flex-col">
                                    <div class="relative overflow-hidden">
                                        <img class="w-full h-48 rounded-t-lg" :src="building.image.view_url">
                                    </div>
                                    <div class="p-4 leading-normal border-b-2 border-gray-300">
                                        <p class="font-semibold">{{ building.address_line_1 | truncate(20) }}</p>
                                        <p class="text-sm">{{ building.city }}, {{ building.state }}  {{ building.postal_code | truncate(5) }}</p>
                                    </div>
                                </div>
                            </div>
                        </a>
                    </li>
                </paginate>
                <paginate-links for="buildings" :show-step-links="true" :hide-single-page="true"></paginate-links>
            </div>

            <div v-if="layout == 'table' && buildings.filtered.length">
                <table class="w-full mb-6">
                    <thead class="cursor-pointer">
                        <tr>
                            <th></th>
                            <th class="pl-5">Address</th>
                            <th class="text-center">LEED</th>
                            <th class="text-center">Class</th>
                            <th class="text-right">Sq. Ft.</th>
                            <th class="text-right">Avail Sq. Ft.</th>
                            <th class="text-right">Floors</th>
                            <th class="text-center">For Sale</th>
                            <th v-if="! isForProject" class="text-center">Last Update</th>
                            <th v-else></th>
                        </tr>
                    </thead>
                    <paginate name="buildings" :list="buildings.filtered" :per="perPage" tag="tbody">
                        <tr v-for="building in paginated('buildings')" :key="building.id">
                            <td class="pl-0 lg:pl-4 hidden lg:table-cell w-16 h-16">
                                <a :href="`#${building.id}`">
                                    <img :src="building.image.view_url" class="mx-auto w-8 h-8 rounded-md shadow-md">
                                </a>
                                <a href="#_" class="lightbox" :id="`${building.id}`">
                                    <img :src="building.image.view_url">
                                </a>
                            </td>
                            <td class="pl-5 leading-normal">
                                <a v-bind:href="building.edit_url" class="block">
                                    <p class="text-sm font-semibold">{{ building.address_line_1 }}</p>
                                    <p class="text-xs">{{ building.city }}, {{ building.state }}  {{ building.postal_code }}</p>
                                </a>
                            </td>
                            <td class="text-center">{{ building.leed_certification }}</td>
                            <td class="text-center">{{ building.class }}</td>
                            <td class="text-right">
                                <span v-if="Number.isInteger(building.total_square_footage)">{{ building.total_square_footage | number }}</span>
                                <span v-else>-</span>
                            </td>
                            <td class="text-right">
                                <span v-if="Number.isInteger(building.available_square_footage)">{{ building.available_square_footage | number }}</span>
                                <span v-else>-</span>
                            </td>
                            <td class="text-right">
                                <span v-if="Number.isInteger(building.floors)">{{ building.floors | number }}</span>
                                <span v-else>-</span>
                            </td>
                            <td>
                                <div class="flex items-center justify-center">
                                    <div v-if="building.for_sale == null">-</div>
                                    <inline-svg v-else-if="building.for_sale" name="check"></inline-svg>
                                    <inline-svg v-else-if="! building.for_sale" name="x"></inline-svg>
                                </div>
                            </td>
                            <td v-if="! isForProject" class="text-center">{{ building.updated_at | date }}</td>
                            <td v-else class="text-right">
                                <button v-if="! isInProject(building.id)" @click="addToProject(building)" class="btn btn-green">Add to Project</button>
                                <button v-else @click="removeFromProject(building)" class="btn btn-red">Remove from Project</button>
                            </td>
                        </tr>
                    </paginate>
                </table>
                <paginate-links for="buildings" :show-step-links="true" :hide-single-page="true"></paginate-links>
            </div>
        </div>

        <div v-if="! searching && ! buildings.filtered.length">
            <div class="none-found">Enter address in the search box on the top right of this screen</div>
        </div>

        <portal-target name="loading"></portal-target>
        <portal-target v-if="searching && ! buildings.filtered.length" name="no-buildings-found"></portal-target>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
        props: {
            showHeader: {
              type: Boolean,
              default: true
            },

            smallHeader: {
              type: Boolean,
              default: false
            },

            showFilters: {
              type: Boolean,
              default: true
            },

            showCreateNew: {
              type: Boolean,
              default: false
            },

            isForProject: {
              type: Boolean,
              default: false
            },

            view: {
                type: String,
                default: 'grid'
            },

            perPage: {
                type: Number,
                default: 10
            }

        },

        data() {
            return {
                paginate: ['buildings'],
                layout: this.view,
                availableSqFtFilter: '',
                forSaleFilter: false,

                searching: false
            }
        },

        computed: {
            ...mapState(['buildings']),
            ...mapState(['projectComps']),

            filters() {
                return {
                    availableSqFtFilter: this.availableSqFtFilter,
                    forSaleFilter: this.forSaleFilter
                }
            }
        },

        methods: {
            getProjectCompId(buildingId) {
                return _.find(this.projectComps.all, ['id', buildingId]).pivot.id;
            },

            isInProject(buildingId) {
                return _.includes(_.map(this.projectComps.all, 'id'), buildingId);
            },

            addToProject(building) {
                this.$emit('add-building-to-project', building);
            },

            removeFromProject(building) {
                const id = this.getProjectCompId(building.id);

                this.$emit('remove-building-from-project', id);
            }
        }
    }
</script>
